import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, CheckIcon } from 'lucide-react';
import { Button } from './ui/Button';
import { useLanguage } from '../contexts/LanguageContext';
import { offeringsCopy } from '../data/translations/offerings';
interface WaitlistDialogProps {
  open: boolean;
  onClose: () => void;
}
export function WaitlistDialog({
  open,
  onClose
}: WaitlistDialogProps) {
  const {
    language
  } = useLanguage();
  const copy = offeringsCopy[language].waitlist;
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);
  useEffect(() => {
    if (open) {
      setSubmitted(false);
      setName('');
      setEmail('');
    }
  }, [open]);
  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitted(true);
  };
  const fieldStyles = 'w-full rounded-lg bg-paper border border-line px-4 py-2.5 text-ink-900 placeholder:text-ink-600/60 focus:outline-none focus:border-bronze-400 transition-colors';
  return <AnimatePresence data-id="element-225" data-mp="vhr5ia2rab2p">
      {open && <div className="fixed inset-0 z-50 flex items-center justify-center px-4" data-id="element-226" data-mp="1s5x3wf93478j">
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease: 'easeOut'
      }} onClick={onClose} className="absolute inset-0 bg-navy/60" data-id="element-227" data-mp="1n6u71i39d7bn" />

          <motion.div role="dialog" aria-modal="true" aria-labelledby="waitlist-title" initial={{
        opacity: 0,
        scale: 0.96
      }} animate={{
        opacity: 1,
        scale: 1
      }} exit={{
        opacity: 0,
        scale: 0.96
      }} transition={{
        duration: 0.22,
        ease: [0.23, 1, 0.32, 1]
      }} className="relative w-full max-w-md rounded-2xl border border-line bg-white p-7 shadow-2xl" data-id="element-228" data-mp="2aw54aplegjl1">
            <button type="button" onClick={onClose} aria-label={copy.close} className="absolute top-4 right-4 text-ink-600 hover:text-bronze-600 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-bronze-400 rounded" data-id="element-229" data-mp="3qmpf2hripvxy">
              <XIcon size={20} data-id="element-230" data-mp="3mn6xnvywo2zv" />
            </button>

            <h2 id="waitlist-title" className="font-serif text-2xl text-ink-900 mb-2" data-id="element-231" data-mp="37p4vnq7ivpkq">
              {copy.title}
            </h2>
            <p className="text-ink-600 text-sm leading-relaxed mb-6" data-id="element-232" data-mp="15x8iyd9mc9od">
              {copy.note}
            </p>

            {submitted ? <div className="flex items-start gap-3 rounded-lg border border-line bg-paper p-4" data-id="element-233" data-mp="1ava2ezayib0d">
                <span className="mt-0.5 text-bronze-600" data-id="element-234" data-mp="2vff45zk93h2z">
                  <CheckIcon size={20} data-id="element-235" data-mp="2qfaoucv2wsec" />
                </span>
                <p className="text-ink-600 text-sm leading-relaxed" data-id="element-236" data-mp="2qgvf3qad1p6e">
                  {copy.success}
                </p>
              </div> : <form onSubmit={handleSubmit} className="space-y-4" data-id="element-237" data-mp="30cylx9g5qbzo">
                <div data-id="element-238" data-mp="30bne8yjlck3a">
                  <label htmlFor="waitlist-name" className="block text-ink-900 text-sm mb-1.5" data-id="element-239" data-mp="3e4c0e8z7kjnu">
                    {copy.nameLabel}
                  </label>
                  <input id="waitlist-name" type="text" required value={name} onChange={event => setName(event.target.value)} placeholder={copy.namePlaceholder} className={fieldStyles} data-id="element-240" data-mp="3o1zwb28jvx7l" />
                </div>

                <div data-id="element-241" data-mp="1q7cld4jxpon1">
                  <label htmlFor="waitlist-email" className="block text-ink-900 text-sm mb-1.5" data-id="element-242" data-mp="1q7vqfkohkijn">
                    {copy.emailLabel}
                  </label>
                  <input id="waitlist-email" type="email" required value={email} onChange={event => setEmail(event.target.value)} placeholder={copy.emailPlaceholder} className={fieldStyles} data-id="element-243" data-mp="206l9vhw16b20" />
                </div>

                <Button type="submit" variant="primary" className="w-full text-base" data-id="element-244" data-mp="1heiqvzvx2iol">
                  {copy.submit}
                </Button>
              </form>}
          </motion.div>
        </div>}
    </AnimatePresence>;
}